import {
  PluginConfigVersion,
  PluginConfigVersionPatch,
  PluginConfigVersionPost,
  PluginRecycleConfiguration,
  PluginRepairConfiguration,
  PluginResaleConfiguration,
  PluginTradeinConfiguration,
  PluginType,
} from '@troverecommerce/ps-configs-api-client';

import { getConfigsApiClient } from './client';
import { mapConfigVersion } from './helpers';

export type PluginConfiguration =
  | PluginRecycleConfiguration
  | PluginRepairConfiguration
  | PluginResaleConfiguration
  | PluginTradeinConfiguration;

export interface IConfigVersionParams {
  id: string;
}

export interface IPartnerCodeParams {
  partnerCode: string;
}

export interface IPluginTypeParams {
  pluginType: PluginType;
}

export interface IPluginParams extends IConfigVersionParams, IPartnerCodeParams, IPluginTypeParams { };

export interface IDeleteConfigVersionParams extends IPluginParams { };

export const deleteConfigVersion = async ({ id, partnerCode, pluginType }: IDeleteConfigVersionParams): Promise<PluginConfigVersion> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.deleteVersion(pluginType, partnerCode, id);
  return mapConfigVersion(data);
};

export interface IGetConfigVersionParams extends IPluginParams { };

export const getConfigVersion = async ({ id, partnerCode, pluginType }: IGetConfigVersionParams): Promise<PluginConfigVersion> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.getVersion(pluginType, partnerCode, id);
  return mapConfigVersion(data);
};

interface IGetConfigVersionsParams extends IPartnerCodeParams, IPluginTypeParams { };

export const getConfigVersions = async ({ partnerCode, pluginType }: IGetConfigVersionsParams): Promise<PluginConfigVersion[]> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.getVersions(pluginType, partnerCode);
  return data.map(d => mapConfigVersion(d));
};

export interface IPostConfigVersionParams extends IPartnerCodeParams, IPluginTypeParams {
  configVersion: PluginConfigVersionPost;
};

export const postConfigVersion = async ({ configVersion, partnerCode, pluginType }: IPostConfigVersionParams): Promise<PluginConfigVersion> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.postVersion(pluginType, partnerCode, {
    data: {
      type: 'pluginConfigVersionPost',
      attributes: configVersion,
    },
  });
  return mapConfigVersion(data);
};

export interface ISetDefaultConfigVersionParams extends IPluginParams { };

export const setDefaultConfigVersion = async ({ id, partnerCode, pluginType }: ISetDefaultConfigVersionParams): Promise<PluginConfigVersion> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.setDefaultVersion(pluginType, partnerCode, {
    data: {
      type: 'pluginDefaultConfigVersionPut',
      id,
    },
  });
  return mapConfigVersion(data);
};

export interface IPatchConfigVersionParams extends IPluginParams {
  configVersion: PluginConfigVersionPatch;
};

export const patchConfigVersion = async ({ configVersion, id, partnerCode, pluginType }: IPatchConfigVersionParams): Promise<PluginConfigVersion> => {
  const configsApi = getConfigsApiClient();
  const { data: { data } } = await configsApi.patchVersion(pluginType, partnerCode, id, {
    data: {
      attributes: {
        name: configVersion.name,
      },
      type: 'pluginConfigVersion',
    },
  });
  return mapConfigVersion(data);
};

interface IGetPluginSettingsParams extends IPluginParams {}

export const getPluginSettings = async ({
  id,
  partnerCode,
  pluginType,
}: IGetPluginSettingsParams): Promise<PluginConfiguration> => {
  const configsApi = getConfigsApiClient();

  switch (pluginType) {
    case 'recycle':
      return (await configsApi.getRecyclePluginSettings(partnerCode, id)).data.data.attributes;
    case 'repair':
      return (await configsApi.getRepairPluginSettings(partnerCode, id)).data.data.attributes;
    case 'resale':
      return (await configsApi.getResalePluginSettings(partnerCode, id)).data.data.attributes;
    case 'tradein':
      return (await configsApi.getTradeinPluginSettings(partnerCode, id)).data.data.attributes;
    default:
      throw new Error(`Unknown pluginType value: ${pluginType satisfies never}`);
  }
};

interface IPutPluginSettingsParams extends IPluginParams {
  settings: PluginConfiguration;
}

export const putPluginSettings = async ({
  settings,
  id,
  partnerCode,
  pluginType,
}: IPutPluginSettingsParams): Promise<PluginConfiguration> => {
  const configsApi = getConfigsApiClient();

  switch (pluginType) {
    case 'recycle':
      return (await configsApi.putRecyclePluginSettings(partnerCode, id, {
        data: { attributes: settings as PluginRecycleConfiguration, type: 'pluginRecycleConfiguration' },
      })).data.data.attributes;

    case 'repair':
      return (await configsApi.putRepairPluginSettings(partnerCode, id, {
        data: { attributes: settings as PluginRepairConfiguration, type: 'pluginRepairConfiguration' },
      })).data.data.attributes;

    case 'resale':
      return (await configsApi.putResalePluginSettings(partnerCode, id, {
        data: { attributes: settings as PluginResaleConfiguration, type: 'pluginResaleConfiguration' },
      })).data.data.attributes;

    case 'tradein':
      return (await configsApi.putTradeinPluginSettings(partnerCode, id, {
        data: { attributes: settings as PluginTradeinConfiguration, type: 'pluginTradeinConfiguration' },
      })).data.data.attributes;

    default:
      throw new Error(`Unknown pluginType value: ${pluginType satisfies never}`);
  }
};